/**
 * PWAUpdatePrompt — banner que aparece quando o service worker detecta
 * versão nova do app (autoUpdate mode). O SW baixa em background; quando
 * pronto, mostramos "Nova versão disponível · Atualizar" pra dar controle
 * ao usuário (não força reload no meio de uma sessão).
 *
 * Também mostra "App pronto pra usar offline" na primeira instalação,
 * pra dar feedback visual de que o PWA foi cacheado.
 *
 * Renderiza null em dev (SW desabilitado via devOptions.enabled=false).
 */

import { useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";
import { RefreshCw, X } from "lucide-react";

export function PWAUpdatePrompt() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    const update = registerSW({
      onNeedRefresh() {
        setNeedRefresh(true);
      },
      onOfflineReady() {
        setOfflineReady(true);
        // auto-esconde depois de 5s — é só feedback informativo
        setTimeout(() => setOfflineReady(false), 5000);
      },
    });
    setUpdateSW(() => update);
  }, []);

  if (!needRefresh && !offlineReady) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-sm">
      {needRefresh && (
        <div className="flex items-center gap-3 rounded-lg border border-cyan-300 bg-cyan-50 px-4 py-3 shadow-lg
                        dark:border-cyan-800 dark:bg-cyan-950/60">
          <RefreshCw className="h-5 w-5 flex-none text-cyan-700 dark:text-cyan-300" />
          <div className="flex-1 text-sm text-cyan-900 dark:text-cyan-100">
            Nova versão disponível
          </div>
          <button
            onClick={() => updateSW?.(true)}
            className="rounded-md bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white
                       hover:bg-cyan-700 transition-colors"
          >
            Atualizar
          </button>
          <button
            onClick={() => setNeedRefresh(false)}
            className="text-cyan-700 hover:text-cyan-900 dark:text-cyan-300"
            aria-label="Dispensar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {offlineReady && !needRefresh && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 shadow-lg
                        dark:border-emerald-800 dark:bg-emerald-950/60">
          <span className="text-sm text-emerald-800 dark:text-emerald-200">
            App pronto pra usar offline
          </span>
        </div>
      )}
    </div>
  );
}
